import React, { useState, useEffect } from 'react';
import { Modal, Select, Button, Row, Col, message, Spin, Divider } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Link } from 'lucide-react';
import { get, post } from '../../utils/fetch';
import styles from './JiraUserMappingModal.module.css';

type OptionType = {
  value: string;
  label: string;
  name?: string;
  email?: string;
};

type Mapping = { asanaUserId: string; bugherdUserId: string };

type BugherdUser = {
  id: string | number;
  display_name?: string;
  name?: string;
  email?: string;
};

interface Props {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  integrationId: string | null;
  projectIntegrationId: string | null;
  workspaceId: string | null;
  bugherdUsers: BugherdUser[];
  container?: HTMLElement | (() => HTMLElement);
}

const emptyMapping: Mapping = { asanaUserId: '', bugherdUserId: '' };

const AsanaUserMappingModal: React.FC<Props> = ({
  open,
  onClose,
  organizationId,
  integrationId,
  projectIntegrationId,
  workspaceId,
  bugherdUsers,
  container
}) => {
  const [loading, setLoading] = useState(false);
  const [asanaUsers, setAsanaUsers] = useState<OptionType[]>([]);
  const [mappings, setMappings] = useState<Mapping[]>([emptyMapping]);

  useEffect(() => {
    if (open && integrationId && workspaceId) {
      loadData();
    }
  }, [open, integrationId, workspaceId]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([fetchAsanaUsers(), fetchCurrentSettings()]);
    setLoading(false);
  };

  const fetchAsanaUsers = async () => {
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/asana_users?id=${integrationId}&workspace_id=${workspaceId}`
      );
      setAsanaUsers(results);
    } catch (error) {
      message.error('Failed to fetch Asana workspace users.');
    }
  };

  const fetchCurrentSettings = async () => {
    if (!projectIntegrationId) {
      setMappings([emptyMapping]);
      return;
    }

    try {
      const result = await get(
        `/organizations/${organizationId}/integrations/${integrationId}/project_integrations/${projectIntegrationId}`
      );
      if (result?.settings?.user_mapping) {
        const existing = Object.entries(result.settings.user_mapping).map(
          ([asanaId, bugherdId]) => ({
            asanaUserId: asanaId,
            bugherdUserId: String(bugherdId)
          })
        );
        setMappings(existing.length ? existing : [emptyMapping]);
      } else {
        setMappings([emptyMapping]);
      }
    } catch (error) {
      message.error('Failed to fetch current user mapping.');
      setMappings([emptyMapping]);
    }
  };

  const saveMappings = async (currentMappings: Mapping[]) => {
    if (!projectIntegrationId) {
      message.error(
        'Unable to save user mappings until the Asana integration settings have been saved.'
      );
      return;
    }

    const usedAsanaUserIds = new Set<string>();
    const usedBugherdUserIds = new Set<string>();
    const userMapping: Record<string, string> = {};
    const userDisplayNames: Record<string, string> = {};

    currentMappings.forEach(({ asanaUserId, bugherdUserId }) => {
      if (
        asanaUserId &&
        bugherdUserId &&
        !usedAsanaUserIds.has(asanaUserId) &&
        !usedBugherdUserIds.has(bugherdUserId)
      ) {
        userMapping[asanaUserId] = bugherdUserId;
        usedAsanaUserIds.add(asanaUserId);
        usedBugherdUserIds.add(bugherdUserId);

        const option = asanaUsers.find(user => user.value === asanaUserId);
        if (option?.name) userDisplayNames[asanaUserId] = option.name;
      }
    });

    try {
      await post(
        `/organizations/${organizationId}/integrations/${integrationId}/project_integrations/save_settings?integration_code=Asana`,
        {
          project_integration_id: projectIntegrationId,
          settings: {
            user_mapping: userMapping,
            user_display_names: userDisplayNames
          }
        }
      );
    } catch (error) {
      message.error('Failed to save user mappings.');
    }
  };

  const handleAutomap = () => {
    const autoPairs: Mapping[] = [];

    asanaUsers.forEach(asanaUser => {
      const remoteLabel = (asanaUser.name || asanaUser.label).toLowerCase();
      const matchedBugherdUser = bugherdUsers.find(
        user =>
          (asanaUser.email &&
            user.email?.toLowerCase() === asanaUser.email.toLowerCase()) ||
          user.display_name?.toLowerCase() === remoteLabel ||
          user.name?.toLowerCase() === remoteLabel ||
          user.email?.toLowerCase() === remoteLabel
      );

      if (matchedBugherdUser) {
        autoPairs.push({
          asanaUserId: asanaUser.value,
          bugherdUserId: String(matchedBugherdUser.id)
        });
      }
    });

    if (autoPairs.length > 0) {
      setMappings(autoPairs);
      saveMappings(autoPairs);
      message.success(`Successfully mapped ${autoPairs.length} users.`);
    } else {
      message.info('No matching users found by name or email.');
    }
  };

  const handleAddMapping = () => {
    setMappings([...mappings, emptyMapping]);
  };

  const handleRemoveMapping = (index: number) => {
    const newMappings = mappings.filter((_, i) => i !== index);
    setMappings(newMappings);
    saveMappings(newMappings);
  };

  const handleMappingChange = (
    index: number,
    field: 'asanaUserId' | 'bugherdUserId',
    value: string
  ) => {
    const newMappings = mappings.map((mapping, currentIndex) =>
      currentIndex === index ? { ...mapping, [field]: value || '' } : mapping
    );
    setMappings(newMappings);

    if (newMappings[index].asanaUserId && newMappings[index].bugherdUserId) {
      saveMappings(newMappings);
    }
  };

  const bugherdUserOptions = bugherdUsers.map(user => ({
    label: user.display_name || user.name || user.email,
    value: String(user.id)
  }));

  const bugherdUserOptionsForMapping = (index: number) => {
    const selectedUserIds = mappings
      .filter((_mapping, mappingIndex) => mappingIndex !== index)
      .map(mapping => mapping.bugherdUserId)
      .filter(Boolean);

    return bugherdUserOptions.filter(
      option => !selectedUserIds.includes(option.value)
    );
  };

  const asanaUserOptionsForMapping = (index: number) => {
    const selectedUserIds = mappings
      .filter((_mapping, mappingIndex) => mappingIndex !== index)
      .map(mapping => mapping.asanaUserId)
      .filter(Boolean);

    return asanaUsers.filter(option => !selectedUserIds.includes(option.value));
  };

  const renderMappingList = () => (
    <div className={styles.mappingList}>
      <div className={styles.mappingRowsContainer}>
        <Row gutter={16} className={styles.headerRow}>
          <Col span={10}>
            <strong>BugHerd User</strong>
          </Col>
          <Col span={2}></Col>
          <Col span={10}>
            <strong>Asana User</strong>
          </Col>
          <Col span={2}></Col>
        </Row>
        {mappings.map((mapping, index) => (
          <Row
            key={index}
            gutter={16}
            className={styles.mappingRow}
            align="middle"
          >
            <Col span={10}>
              <Select
                showSearch
                style={{ width: '100%' }}
                placeholder="Search BugHerd user"
                optionFilterProp="label"
                options={bugherdUserOptionsForMapping(index)}
                value={mapping.bugherdUserId || undefined}
                onChange={value =>
                  handleMappingChange(index, 'bugherdUserId', value)
                }
                allowClear
              />
            </Col>
            <Col span={2} style={{ textAlign: 'center' }}>
              <Link size={16} className={styles.linkIcon} />
            </Col>
            <Col span={10}>
              <Select
                showSearch
                style={{ width: '100%' }}
                placeholder="Search Asana user"
                optionFilterProp="label"
                options={asanaUserOptionsForMapping(index)}
                value={mapping.asanaUserId || undefined}
                onChange={value =>
                  handleMappingChange(index, 'asanaUserId', value)
                }
                allowClear
              />
            </Col>
            <Col span={2}>
              <Button
                type="text"
                icon={<MinusCircleOutlined />}
                onClick={() => handleRemoveMapping(index)}
                danger
              />
            </Col>
          </Row>
        ))}
        <div className={styles.addMapping}>
          <Button
            type="link"
            onClick={handleAddMapping}
            icon={<PlusOutlined />}
          >
            Sync another user
          </Button>
        </div>
      </div>
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <Divider plain>or</Divider>
        <Button
          type="primary"
          size="large"
          onClick={handleAutomap}
          style={{ padding: '0 40px' }}
        >
          Automap BugHerd to Asana users
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      title="Manage users between BugHerd and Asana"
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
      getContainer={container}
    >
      <div className={styles.modalContent}>
        <p>
          This will allow you to match the users that are assigned and
          @mentioned on tasks across BugHerd and Asana.
        </p>

        {loading ? (
          <div className={styles.loader}>
            <Spin />
          </div>
        ) : (
          renderMappingList()
        )}
      </div>
    </Modal>
  );
};

export default AsanaUserMappingModal;
